// @flow

import FormRow from 'components/FormRow';
import withQueryClient from 'components/hoc/withQueryClientService';
import { TextInputForm } from 'components/TextInput';
import View from 'components/View';
import { Form, withFormik } from 'formik';
import gql from 'graphql-tag';
import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router';
import { compose } from 'recompose';
import * as Yup from 'yup';
import { withCookies } from 'react-cookie';
import { path } from 'rambda';
import { injectIntl } from 'react-intl';
import { toast } from 'react-toastify';
import moment from 'moment';
import Date from '../../components/Date';
import { CheckboxForm } from '../components/Checkbox';
import Button from '../../components/Button';
import { cookiesSetting, INF_NOTIFICATIONS, USER_ID } from '../../common/constants';
import { serverInternalError, serverValidationErrors } from '../../utils/forms';
import CountriesSearch from '../../components/CountriesSearch';

const initialValues = {
  name: '',
  surname: '',
  bornAt: '',
  countryId: '',
  email: '',
  phoneNumber: '',
  address: '',
  infMail: [],
  infNotifications: [],
  password: '',
  passwordConfirmation: ''
};

const query = gql`
  query($userId: String!) {
    users(record: $userId) {
      edges {
        node {
          id: record
          status
          name
          surname
          bornAt
          countryId
          email
          phoneNumber
          address
          infMail
        }
      }
    }
  }
`;

const mutation = gql`
  mutation($user: UserInput!) {
    user(data: $user) {
      user {
        id: record
      }
    }
  }
`;

const onSubmit = (
  {
    tools: { client, intl },
    id,
    status,
    name,
    surname,
    bornAt,
    countryId,
    email,
    phoneNumber,
    address,
    infMail,
    infNotifications,
    onClose,
    password
  },
  { setSubmitting, setStatus, props: { cookies } }
) => {
  const variables = {
    user: {
      record: id,
      status,
      name,
      surname,
      bornAt: bornAt || null,
      countryId,
      email,
      phoneNumber,
      address,
      infMail: infMail.includes('infMail'),
      password
    }
  };

  const infNotificationsBooleanValue = infNotifications.includes('infNotifications');

  client
    .mutate({
      mutation,
      variables
    })
    .then(() => {
      onClose();
      toast.success(intl.formatMessage({ id: 'main.profile.form.saved' }), {
        autoClose: 5000,
        position: 'top-center'
      });
      client.reFetchObservableQueries();
      setTimeout(() => {
        const infNotificationsValue = infNotificationsBooleanValue || false;
        cookies.set(INF_NOTIFICATIONS, infNotificationsValue, cookiesSetting);
      }, 1000);
    })
    .catch(e => {
      setSubmitting(false); // because isSubmitting needs this
      const errors = serverValidationErrors(e) || {};
      errors.__submitError =
        serverInternalError(e) || intl.formatMessage({ id: 'errors.unable-to-save-form' });

      setStatus(errors);
    });
};

const validationSchema = intl =>
  Yup.object().shape({
    name: Yup.string().required(intl.formatMessage({ id: 'main.profile.form.required' })),
    surname: Yup.string().required(intl.formatMessage({ id: 'main.profile.form.required' })),
    email: Yup.string().required(intl.formatMessage({ id: 'main.profile.form.required' })),
    passwordConfirmation: Yup.string().test(
      'passwords-match',
      intl.formatMessage({ id: 'main.profile.form.passwordsMustMatch' }),
      function(value) {
        return this.parent.password === value;
      }
    )
  });

const styles = {
  rowStyle: {
    display: 'flex',
    width: '420px',
    flexDirection: 'row',
    justifyContent: 'space-between'
  }
};

const ProfileData = ({ setStatus, client, cookies, values, setValues, intl }) => {
  const [showCountries, setShowCountries] = useState(false);
  const [showBornAt, setShowBornAt] = useState(false);

  const loadProfileData = () => {
    const variables = { userId: cookies.get(USER_ID) };

    client
      .query({ query, variables })
      .then(({ data }) => {
        const user = path('users.edges.0.node', data);

        const loadedData = {
          ...user,
          bornAt: user.bornAt || '',
          countryId: user.countryId || '',
          infMail: user.infMail ? ['infMail'] : [],
          // infNotifications: user.infNotifications ? ['infNotifications'] : []
          infNotifications: cookies.get(INF_NOTIFICATIONS) === 'true' ? ['infNotifications'] : []
        };
        setValues({ ...values, ...loadedData });
        setShowCountries(true);
        setStatus({ loading: false });
        setShowBornAt(true);
      })
      .catch(err => setStatus({ error: err.message }));
  };

  useEffect(() => {
    loadProfileData();
  }, []);

  return (
    <Form>
      <View
        style={{
          maxHeight: '80vh',
          overflowY: 'auto'
        }}>
        <View
          isExpanded
          isVertical
          style={{
            paddingHorizontal: 40,
            paddingVertical: 10,
            alignItems: 'center',
            width: '100%'
          }}>
          <FormRow>
            <View style={styles.rowStyle}>
              <TextInputForm
                name="name"
                label={intl.formatMessage({ id: 'main.profile.form.name' })}
                styleBox={{ flexDirection: 'column', alignItems: 'flex-start' }}
                style={{ width: '200px' }}
                isVertical
              />
              <View style={{ width: '20px' }} />
              <TextInputForm
                name="surname"
                label={intl.formatMessage({ id: 'main.profile.form.surname' })}
                styleBox={{ flexDirection: 'column', alignItems: 'flex-start' }}
                style={{ width: '200px' }}
                isVertical
              />
            </View>
          </FormRow>
          <FormRow>
            <View style={styles.rowStyle}>
              <View style={{ width: 200 }}>
                {showBornAt && (
                  <Date
                    isVertical
                    label={intl.formatMessage({ id: 'main.profile.form.bornAt' })}
                    name="bornAt"
                    width={200}
                    initialDate={values.bornAt}
                    onDateChange={d => {
                      if (d) {
                        setValues({ ...values, bornAt: moment(d).format('YYYY-MM-DD') });
                      }
                    }}
                  />
                )}
              </View>
              <View style={{ width: '20px' }} />
              {showCountries && (
                <View style={{ width: '200px' }}>
                  <CountriesSearch values={values} />
                </View>
              )}
            </View>
          </FormRow>
          <FormRow>
            <View style={styles.rowStyle}>
              <TextInputForm
                name="email"
                label={intl.formatMessage({ id: 'main.profile.form.email' })}
                styleBox={{ flexDirection: 'column', alignItems: 'flex-start' }}
                style={{ width: '200px' }}
                isVertical
              />
              <View style={{ width: '20px' }} />
              <TextInputForm
                name="phoneNumber"
                label={intl.formatMessage({ id: 'main.profile.form.phone' })}
                styleBox={{ flexDirection: 'column', alignItems: 'flex-start' }}
                style={{ width: '200px' }}
                isVertical
              />
            </View>
          </FormRow>
          <FormRow>
            <View style={styles.rowStyle}>
              <TextInputForm
                name="address"
                label={intl.formatMessage({ id: 'main.profile.form.address' })}
                styleBox={{ flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}
                style={{ width: '420px' }}
                isVertical
              />
            </View>
          </FormRow>
          <FormRow>
            <View style={styles.rowStyle}>
              <TextInputForm
                name="password"
                type="password"
                label={intl.formatMessage({ id: 'main.profile.form.password' })}
                styleBox={{ flexDirection: 'column', alignItems: 'flex-start' }}
                style={{ width: '200px' }}
                isVertical
              />
              <View style={{ width: '20px' }} />
              <TextInputForm
                name="passwordConfirmation"
                type="password"
                label={intl.formatMessage({ id: 'main.profile.form.passwordConfirmation' })}
                styleBox={{ flexDirection: 'column', alignItems: 'flex-start' }}
                style={{ width: '200px' }}
                isVertical
              />
            </View>
          </FormRow>
          <FormRow>
            <View style={styles.rowStyle}>
              <CheckboxForm
                name="infMail"
                value="infMail"
                label={intl.formatMessage({ id: 'main.profile.form.infMail' })}
              />
            </View>
          </FormRow>
          <FormRow>
            <View style={styles.rowStyle}>
              <CheckboxForm
                name="infNotifications"
                value="infNotifications"
                label={intl.formatMessage({ id: 'main.profile.form.infNotifications' })}
              />
            </View>
          </FormRow>
          <FormRow>
            <Button
              label={intl.formatMessage({ id: 'main.profile.form.save' })}
              icon="check"
              scale={-0.5}
              accent="secondary"
            />
          </FormRow>
        </View>
      </View>
    </Form>
  );
};

export default compose(
  injectIntl,
  withQueryClient,
  withRouter,
  withCookies,
  withFormik({
    mapPropsToValues: ({ client, history: { push }, onClose, intl }) => ({
      tools: {
        client,
        push,
        intl
      },
      ...initialValues,
      onClose
    }),
    handleSubmit: onSubmit,
    validationSchema: ({ intl }) => validationSchema(intl)
  })
)(ProfileData);
